import {
  forwardRef,
  memo,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { CollapsibleTxtMsg } from './collapsible-txt-msg';
import { FieldChecker, FieldCheckerProps } from './field-checker';
import { EditIcon, LetterAIcon } from './bs-icons';
import { FieldValueChecker, FieldValueCheckerProps } from './field-value-checker';
import { SquareChecker } from './square-checker';
import { cn } from '../lib/utils';

export interface FieldCheckerListMsgRef {
  getCheckedFieldsLength: () => number;
}

export const FieldCheckerListMsg = memo(
  forwardRef<
    FieldCheckerListMsgRef,
    {
      fields: Array<FieldCheckerProps>;
      children?: ReactNode;
      streaming: boolean;
      confirmed?: boolean;
    }
  >(({ fields, children, streaming, confirmed }, ref) => {
    // Track individual field checked states (all default to true)
    const [checkedMap, setCheckedMap] = useState<Record<string, boolean>>(() => {
      const map: Record<string, boolean> = {};
      fields.forEach((f) => {
        map[f.field?.label ?? ''] = true;
      });
      return map;
    });

    const allChecked = useMemo(
      () => fields.length > 0 && fields.every((f) => checkedMap[f.field?.label ?? ''] !== false),
      [checkedMap, fields]
    );

    const someChecked = useMemo(
      () => fields.some((f) => checkedMap[f.field?.label ?? ''] !== false),
      [checkedMap, fields]
    );

    const intermediate = streaming || (!allChecked && someChecked);

    useImperativeHandle(
      ref,
      () => ({
        getCheckedFieldsLength: () =>
          fields.filter((f) => checkedMap[f.field?.label ?? ''] !== false).length,
      }),
      [checkedMap, fields]
    );

    const handleToggleAll = useCallback(
      (checked: boolean | 'indeterminate') => {
        const value = checked === true;
        const newMap: Record<string, boolean> = {};
        fields.forEach((f) => {
          newMap[f.field?.label ?? ''] = value;
        });
        setCheckedMap(newMap);
        fields.forEach((f) => f.onChange?.(value, f.field));
      },
      [fields]
    );

    return (
      <CollapsibleTxtMsg title="字段生成" icon={<LetterAIcon />} defaultOpen>
        {
          <div
            className={cn(
              'flex gap-2.5 px-4 py-2.5 rounded-[4px] w-full transition-colors cursor-pointer hover:bg-[#EFF0F6] items-center',
              {
                'pointer-events-none': confirmed || streaming,
              }
            )}
            onClick={() => {
              if (confirmed) {
                return;
              }

              const newChecked = !allChecked;
              handleToggleAll(newChecked);
            }}
          >
            <SquareChecker
              readonly={confirmed || streaming}
              checked={allChecked && !streaming}
              onCheckedChange={handleToggleAll}
              intermediate={intermediate}
            >
              <span className="text-[#0265ff]! font-bold!">全选</span>
            </SquareChecker>
          </div>
        }
        {fields.map((f) => (
          <FieldChecker
            {...f}
            key={f.field?.label}
            checked={checkedMap[f.field?.label ?? '']}
            streaming={streaming}
            confirmed={confirmed}
            onChange={(checked, field, parentFieldName, index) => {
              const isSubField = index !== undefined && parentFieldName !== undefined;
              if (!isSubField) {
                setCheckedMap((prev) => ({ ...prev, [f.field?.label ?? '']: checked }));
              }
              f.onChange?.(checked, field, parentFieldName, index);
            }}
          />
        ))}
        {children}
      </CollapsibleTxtMsg>
    );
  })
);

export const FieldValueCheckerListMsg = memo(
  ({
    props,
    readonly,
    children,
    streaming,
  }: {
    props: Array<FieldValueCheckerProps>;
    children?: ReactNode;
    readonly?: boolean;
    streaming?: boolean;
  }) => {
    return (
      <CollapsibleTxtMsg title="字段填写" icon={<EditIcon />} defaultOpen>
        {props.map((f) => (
          <FieldValueChecker {...f} readonly={readonly} streaming={streaming} />
        ))}
        {children}
      </CollapsibleTxtMsg>
    );
  }
);
